/**
 * SSE consumer hook used by ChatDock, TickerTape, and quote stream.
 *
 * Differences from a naive EventSource:
 *  - works with POST + body (chat/send) by using fetch + ReadableStream
 *  - exposes typed event handlers
 *  - cleanly aborts when the consumer unmounts or `enabled` flips false
 */
import { useEffect, useRef, useState } from 'react';

export type SseEventHandler = (event: { event: string; data: unknown }) => void;

export interface UseStreamOpts {
  url: string;
  method?: 'GET' | 'POST';
  body?: unknown;
  headers?: Record<string, string>;
  enabled?: boolean;
  onEvent?: SseEventHandler;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Error) => void;
}

export interface UseStreamResult {
  status: 'idle' | 'open' | 'closed' | 'error';
  abort: () => void;
}

function parseSseChunk(buf: string): { event: string; data: unknown }[] {
  const out: { event: string; data: unknown }[] = [];
  for (const block of buf.split('\n\n')) {
    if (!block.trim()) continue;
    let event = 'message';
    let dataStr = '';
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataStr += line.slice(5).trim();
      // ignore comments (": keepalive ...")
    }
    let data: unknown = dataStr;
    try {
      data = JSON.parse(dataStr);
    } catch {
      // not JSON, leave as string
    }
    out.push({ event, data });
  }
  return out;
}

export function useStream(opts: UseStreamOpts): UseStreamResult {
  const [status, setStatus] = useState<UseStreamResult['status']>('idle');
  const ctrlRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (opts.enabled === false) return;
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    let buf = '';
    (async () => {
      try {
        setStatus('open');
        opts.onOpen?.();
        const r = await fetch(opts.url, {
          method: opts.method ?? 'GET',
          credentials: 'include',
          headers: {
            Accept: 'text/event-stream',
            ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
            ...(opts.headers ?? {}),
          },
          body: opts.body ? JSON.stringify(opts.body) : undefined,
          signal: ctrl.signal,
        });
        if (!r.ok || !r.body) {
          throw new Error(`stream http ${r.status}`);
        }
        const reader = r.body.getReader();
        const decoder = new TextDecoder();
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          // Split on full SSE event boundaries (\n\n)
          const lastBoundary = buf.lastIndexOf('\n\n');
          if (lastBoundary !== -1) {
            const ready = buf.slice(0, lastBoundary);
            buf = buf.slice(lastBoundary + 2);
            for (const evt of parseSseChunk(ready)) {
              opts.onEvent?.(evt);
            }
          }
        }
        if (buf.trim()) {
          for (const evt of parseSseChunk(buf)) opts.onEvent?.(evt);
        }
        setStatus('closed');
        opts.onClose?.();
      } catch (e: unknown) {
        if ((e as { name?: string })?.name === 'AbortError') {
          setStatus('closed');
          return;
        }
        setStatus('error');
        opts.onError?.(e as Error);
      }
    })();
    return () => {
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.url, opts.method, opts.enabled, JSON.stringify(opts.body ?? null)]);

  return {
    status,
    abort: () => ctrlRef.current?.abort(),
  };
}
